import * as React from "react";
import { useOpeningBalancesMatrixRange } from "@/hooks/finance/reconciliation/useStartingBalance";
import { useTxInOutMatrixRange } from "@/hooks/finance/reconciliation/useBankTransactionsInAndOut";
import { usePaidExpensesMatrixRange } from "@/hooks/finance/reconciliation/useExpenses";
import { useInternalTransfersMatrixRange } from "@/hooks/finance/reconciliation/useInternalTransfersMatrix";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
};

export type LedgerExpectedRow = {
  ledger_expected_closing: number | null;

  opening_balance: number | null;
  opening_balance_ts: string | null;

  credit_in: number;
  fee_total: number;
  paid_expense_total: number;
  transfer_out: number;
  transfer_in: number;

  currency: string | null;
};

export type LedgerExpectedMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, LedgerExpectedRow>>;
};

export function useLedgerExpectedMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo } = args;

  const openingQ = useOpeningBalancesMatrixRange(args);

  const enableSubs =
    bankAccountIds.length > 0 &&
    !!dateFrom &&
    !!dateTo &&
    !!openingQ.data;

  const txQ = useTxInOutMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: enableSubs,
  });

  const expensesQ = usePaidExpensesMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: enableSubs,
  });

  const transfersQ = useInternalTransfersMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled: enableSubs,
  });

  const data = React.useMemo<LedgerExpectedMatrix | undefined>(() => {
    if (!openingQ.data || !txQ.data || !expensesQ.data || !transfersQ.data) return undefined;

    const dateKeys = openingQ.data.dateKeys;
    const byAccount = new Map<string, Map<string, LedgerExpectedRow>>();

    for (const accId of bankAccountIds) {
      const rowMap = new Map<string, LedgerExpectedRow>();

      const openMap = openingQ.data.byAccount.get(accId);
      const txMap = txQ.data.byAccount.get(accId);
      const expMap = expensesQ.data.byAccount.get(accId);
      const trMap = transfersQ.data.byAccount.get(accId);

      for (const dayKey of dateKeys) {
        const open = openMap?.get(dayKey);
        const tx = txMap?.get(dayKey);
        const exp = expMap?.get(dayKey);
        const tr = trMap?.get(dayKey);

        const opening_balance = open?.opening_balance ?? null;
        const credit_in = tx?.in_amount ?? 0;
        const fee_total = tx?.fee_total ?? 0;
        const paid_expense_total = exp?.paid_total ?? 0;
        const transfer_out = tr?.transfer_out ?? 0;
        const transfer_in = tr?.transfer_in ?? 0;

        const ledger_expected_closing =
          opening_balance == null
            ? null
            : opening_balance + credit_in + transfer_in - paid_expense_total - fee_total - transfer_out;

        rowMap.set(dayKey, {
          ledger_expected_closing,
          opening_balance,
          opening_balance_ts: open?.opening_balance_ts ?? null,
          credit_in,
          fee_total,
          paid_expense_total,
          transfer_out,
          transfer_in,
          currency: open?.currency ?? tx?.currency ?? exp?.currency ?? null,
        });
      }

      byAccount.set(accId, rowMap);
    }

    return { dateKeys, byAccount };
  }, [openingQ.data, txQ.data, expensesQ.data, transfersQ.data, bankAccountIds]);

  return {
    data,
    isLoading: openingQ.isLoading || txQ.isLoading || expensesQ.isLoading || transfersQ.isLoading,
    isError: openingQ.isError || txQ.isError || expensesQ.isError || transfersQ.isError,
    error: openingQ.error ?? txQ.error ?? expensesQ.error ?? transfersQ.error ?? null,
    refetch: async () => {
      await Promise.all([openingQ.refetch(), txQ.refetch(), expensesQ.refetch(), transfersQ.refetch()]);
    },
  };
}